import {
  DestroyRef,
  Injectable,
  Signal,
  computed,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';

import { fromWire, toWire } from '@core/filters/path-filter/codec';
import { andFilters } from '@core/filters/path-filter/combine';
import {
  DraftGroup,
  DraftNode,
  andChild,
  draftCondition,
  draftGroup,
  orChild
} from '@core/filters/path-filter/draft';
import { SearchBarState, SortQualifier } from '@core/filters/path-filter/dsl/search-bar';
import { holdsHoles, templatePreviewTree } from '@core/filters/path-filter/templates';
import {
  uidMembershipField,
  uidMembershipLiterals
} from '@core/filters/path-filter/uid-membership';
import { shorthandCodec } from '@core/filters/path-filter/vocabulary/default-operators';
import {
  FilterField,
  FilterVocabulary,
  decodeOptionsFor
} from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  FieldPath,
  FilterExpr,
  fieldPath,
  pathKey,
  segmentName
} from '@core/filters/path-filter/wire';
import { FilterPresetPayload } from '@core/models/filter-preset.model';
import { GbeError } from '@core/models/gbe/gbe-error.model';
import { FilterGrammarService } from '@core/services/filter-grammar/filter-grammar.service';
import { PathFilterVocabularyService } from '@core/services/path-filter-vocabulary/path-filter-vocabulary.service';
import { FilterGrammar } from '@core/store/user-preferences/user-preferences.model';
import { namesWord, sameJson } from '@core/utils/shared-utils';

import {
  toOrderBy,
  toSortQualifier
} from '@shared/components/path-filter/sort/sort-options';
import { SearchConfig } from '@shared/components/toolbox/toolbox.component';
import { buildPathFilterPresetPayload } from '@shared/utils/filter-preset.utils';

import { AppliedFilterPreset } from '@features/components/entities/filter-presets/filter-presets.store';

/** What the Path grammar adds to a data request. */
export interface PathFetch {
  filter?: FilterExpr;
  search: string;
  sort: SortQualifier[];
}

/** The host side of an applied preset. */
export interface PresetTarget {
  /** The preset is in effect on the bar; the host applies its Classic part, sort and paging. */
  apply(applied: AppliedFilterPreset): void;
  /** A Path preset this bar cannot show. */
  refuse(): void;
}

/**
 * The field a root child filters on as a column filter: a condition, or a None group negating one
 * condition alone, which is how a negated condition is decoded.
 */
function columnConditionPath(node: DraftNode): FieldPath | null {
  if (node.kind === 'condition') return node.path;
  if (
    node.kind === 'group' &&
    node.negated &&
    node.connector === 'or' &&
    node.children.length === 1 &&
    node.children[0].kind === 'condition'
  ) {
    return node.children[0].path;
  }
  return null;
}

/** `pathKey`s of the conditions whose dotted path the error message names as a whole word. */
export function invalidPathsFor(message: string | null, root: DraftGroup): string[] {
  if (!message) return [];
  const keys = new Set<string>();
  const visit = (node: DraftNode): void => {
    if (node.kind === 'group') node.children.forEach(visit);
    else if (node.kind === 'association') visit(node.filter);
    else if (node.kind === 'condition' && node.path) {
      if (namesWord(message, node.path.segments.map(segmentName).join('.'))) {
        keys.add(pathKey(node.path));
      }
    }
  };
  visit(root);
  return [...keys];
}

/**
 * One filter bar's Path state and grammar. Two filters are ANDed in every request: the composer's,
 * which the pills and the panel edit and presets store, and the search bar's quick filter, which
 * stays out of presets.
 */
@Injectable()
export class PathFilterBarState {
  private readonly grammarService = inject(FilterGrammarService);
  private readonly vocabularies = inject(PathFilterVocabularyService);

  readonly entityType = signal('');
  readonly forcedGrammar = signal<FilterGrammar | null>(null);
  /** For a host whose rows are not a catalogue entity. */
  readonly ownVocabulary = signal<FilterVocabulary | null>(null);
  /** For a host that needs more than the catalogue offers; a refused vocabulary means Classic. */
  readonly acceptsVocabulary = signal<(vocabulary: FilterVocabulary) => boolean>(
    () => true
  );
  /**
   * Set by an applied preset; local to the bar, never saved as the preference. Outlives a hand
   * edit while the filter is still on screen, and goes once the bar shows nothing to lose.
   */
  readonly presetGrammar = signal<FilterGrammar | null>(null);
  /** For a host that applies presets: whether its Classic bar holds conditions or free text. */
  readonly classicFiltered = signal<() => boolean>(() => false);
  /**
   * The grammar this bar keeps whatever the preference says: the preference as it last took it once
   * a switch happened while it was open, or the grammar of a preset whose filter it moved between
   * sides. `null` while it still follows the preference, which may not have loaded yet.
   */
  private readonly ownGrammar = signal<FilterGrammar | null>(null);
  private readonly wantedGrammar = computed(
    () => this.presetGrammar() ?? this.ownGrammar() ?? this.grammarService.grammar()
  );

  readonly draft = signal<DraftGroup>(draftGroup());
  /** The Sort tab's list. */
  readonly sort = signal<SortQualifier[]>([]);
  /** The search bar's quick filter, as typed. */
  readonly bar = signal<SearchBarState>({ search: [], sort: [] });
  readonly error = signal<string | null>(null);
  readonly pathPanel = signal<'filter' | 'sort' | null>(null);

  private editHandler: () => void = () => undefined;
  private barEditHandler: () => void = () => undefined;

  private readonly catalogueVocabulary = this.vocabularies.vocabularyFor(this.entityType);

  readonly vocabulary = computed<FilterVocabulary | null>(() => {
    const vocabulary = this.ownVocabulary() ?? this.catalogueVocabulary();
    return vocabulary && this.acceptsVocabulary()(vocabulary) ? vocabulary : null;
  });

  /** A `null` vocabulary is final: "no Path here", not "not loaded yet". */
  private readonly vocabularyKnown = computed(
    () => this.ownVocabulary() !== null || this.vocabularies.catalogueLoaded()
  );

  readonly activeGrammar = computed<FilterGrammar>(() => {
    const forced = this.forcedGrammar();
    if (forced) return forced;
    if (!this.grammarService.available() || !this.vocabulary()) return 'classic';
    return this.wantedGrammar();
  });

  /** The bar could show a Path preset. */
  readonly pathPossible = computed(() => {
    const forced = this.forcedGrammar();
    if (forced) return forced === 'path';
    return this.grammarService.available() && this.vocabulary() !== null;
  });

  /** `activeGrammar` and `pathPossible` are final. */
  readonly settled = computed(
    () =>
      this.forcedGrammar() !== null ||
      (this.grammarService.settled() && this.vocabularyKnown())
  );

  /** Fetching now would send a Classic request, then a Path one once the vocabulary arrives. */
  readonly fetchHeld = computed(() => {
    if (this.forcedGrammar() !== null) return false;
    if (!this.grammarService.settled()) return true;
    return (
      this.grammarService.available() &&
      this.wantedGrammar() === 'path' &&
      !this.vocabularyKnown()
    );
  });

  /**
   * A tree loaded before the vocabulary: decoding it has to wait, because the vocabulary
   * decides which conditions are editable and which are kept verbatim.
   */
  private readonly pending = signal<{ expr: FilterExpr | undefined } | null>(null);
  /**
   * The last loaded tree and the draft decoded from it. Re-encoding an untouched draft can
   * rewrite a tree spelled differently from the codec's canonical form, and a preset saved over
   * without an edit must store what it loaded.
   */
  private readonly loaded = signal<{
    expr: FilterExpr | undefined;
    draft: DraftGroup;
  } | null>(null);

  readonly wire = computed<FilterExpr | undefined>(
    () => {
      const pending = this.pending();
      if (pending) return pending.expr;
      const draft = this.draft();
      const loaded = this.loaded();
      return loaded?.draft === draft ? loaded.expr : toWire(draft);
    },
    { equal: sameJson }
  );

  /** A Path preset that arrived before the flags or the catalogue; replayed once `settled`. */
  private readonly heldPreset = signal<{
    applied: AppliedFilterPreset;
    target: PresetTarget;
  } | null>(null);

  /** The composer's tree ANDed with the bar's, holes included: what the rows shown match. */
  readonly filter = computed<FilterExpr | undefined>(
    () => andFilters(this.wire(), this.bar().expr),
    { equal: sameJson }
  );

  /** Either tree holds a template hole, which no write to "every matching row" may guess at. */
  readonly holed = computed(() => holdsHoles(this.wire()) || holdsHoles(this.bar().expr));

  /** What the user set, holes included; the search bar re-emits an unchanged filter. */
  private readonly request = computed<PathFetch | null>(
    () =>
      this.activeGrammar() === 'path'
        ? {
            filter: this.filter(),
            search: this.bar().search.join(' '),
            sort: [...this.bar().sort, ...this.sort()]
          }
        : null,
    { equal: sameJson }
  );

  /** Set by a host that fills holes itself before querying, as a recipe preview does with samples. */
  readonly fillsHoles = signal(false);

  /**
   * What hosts query with, refetching whenever it changes: a hole is never sent as a value, whether
   * it was typed while authoring a template or before. A host that fills holes fills the
   * composer's only, the parameters of what it previews; the bar's are always left out.
   */
  readonly fetch = computed<PathFetch | null>(
    () => {
      const request = this.request();
      if (!request) return null;
      const composer = this.wire();
      const typed = this.bar().expr;
      return {
        ...request,
        filter: andFilters(
          composer && !this.fillsHoles() ? templatePreviewTree(composer) : composer,
          typed && templatePreviewTree(typed)
        )
      };
    },
    { equal: sameJson }
  );

  /** The composer side holds nothing a preset would store; the bar plays no part. */
  readonly isEmpty = computed(() => this.wire() === undefined && this.sort().length === 0);

  readonly invalidPaths = computed(() => invalidPathsFor(this.error(), this.draft()));

  /** The root-level field a table column filters on, given the column's path. */
  fieldAt(...segments: string[]): FilterField | undefined {
    return this.vocabulary()?.field(null, fieldPath(...segments));
  }

  /**
   * The root-level field of a column, given its DB name. A dotted name is one segment for a
   * metadata key (`metadata.due_date`) and one segment per dot for a nested field
   * (`nb_pallets.PP`); the vocabulary is what tells them apart.
   */
  fieldForColumn(dbColName: string): FilterField | undefined {
    return (
      this.fieldAt(dbColName) ??
      (dbColName.includes('.') ? this.fieldAt(...dbColName.split('.')) : undefined)
    );
  }

  /**
   * The fields root-level conditions filter on, dotted as column DB names spell them, which is
   * what marks a column header as filtered. Empty outside Path.
   */
  readonly filteredColumnNames = computed<ReadonlySet<string>>(() => {
    if (this.activeGrammar() !== 'path') return new Set();
    return new Set(
      this.draft().children.flatMap((child) => {
        const path = columnConditionPath(child);
        return path ? [path.segments.map(segmentName).join('.')] : [];
      })
    );
  });

  /**
   * Filters on a list value clicked in a cell, replacing the root condition an earlier click left on
   * the field, as Classic does: ANDed, two clicks would ask for rows holding both values. `false`
   * when the field's shorthand cannot spell the value, which is then left for the user to finish
   * in the pill popover.
   */
  addListItem(field: FilterField, item: string): boolean {
    const vocabulary = this.vocabulary();
    const codec = vocabulary ? shorthandCodec(field, vocabulary) : null;
    const arg = codec?.encode({ kind: 'list', items: [{ raw: item, literal: null }] });
    if (!codec || !arg) return false;
    const condition = draftCondition(field.path, codec.op, arg);
    const draft = this.draft();
    const index = draft.children.findIndex(
      (child) =>
        child.kind === 'condition' &&
        !child.negated &&
        child.op === codec.op &&
        child.path !== null &&
        pathKey(child.path) === field.key
    );
    const replaced = draft.connector === 'and' && !draft.negated && index !== -1;
    this.editDraft(
      replaced
        ? {
            ...draft,
            children: draft.children.map((child, i) => (i === index ? condition : child))
          }
        : andChild(draft, condition)
    );
    return true;
  }

  /** The root uid leaf, which names rows by uid; absent where the vocabulary has none. */
  readonly uidField = computed(() => {
    const vocabulary = this.vocabulary();
    return vocabulary ? uidMembershipField(vocabulary) : undefined;
  });

  /**
   * "Include in filter": ORs `uid` into the filter, so every row it matched stays matched. The bar's
   * filter terms move into the composer first: left in the bar, they would stay ANDed in front of
   * the OR and keep hiding the row. Free text cannot move, which `shownPayload` reports.
   */
  includeUid(uid: string): void {
    const field = this.uidField();
    if (!field) return;
    const condition = draftCondition(
      field.path,
      'is_any_of',
      uidMembershipLiterals(field, [uid])
    );
    const withBar = this.draftWithBar();
    if (!withBar) {
      this.editDraft(orChild(this.draft(), condition));
      return;
    }
    this.moveBetweenSides(() => this.takeBarInto(orChild(withBar, condition)));
  }

  /** What the rows shown match, the bar's filter and text included, for judging an include. */
  shownPayload(): FilterPresetPayload {
    const filter = this.filter();
    const search = this.bar().search.join(' ');
    return { ...(filter ? { pathFilter: filter } : {}), ...(search ? { search } : {}) };
  }

  /** Drops the root-level conditions on `field`, as a column header's remove asks. */
  removeConditionsOn(field: FilterField): void {
    const draft = this.draft();
    this.editDraft({
      ...draft,
      children: draft.children.filter((child) => {
        const path = columnConditionPath(child);
        return !(path && pathKey(path) === field.key);
      })
    });
  }

  /** What saving a preset from this bar would store: the composer side, never the search bar. */
  presetPayload(): FilterPresetPayload {
    return buildPathFilterPresetPayload(this.wire(), this.sort().map(toOrderBy));
  }

  /** The toolbox search box, which the Path search bar replaces. */
  toolboxSearch(classic: () => SearchConfig): Signal<SearchConfig> {
    return computed(() =>
      this.activeGrammar() === 'path' ? { active: false } : classic()
    );
  }

  /**
   * Runs after a hand edit of the composer that changed the request: the host refetches and
   * detaches its preset.
   */
  onEdit(handler: () => void): void {
    this.editHandler = handler;
  }

  /** Runs after a search-bar edit that changed the request: the host refetches, keeping its preset. */
  onBarEdit(handler: () => void): void {
    this.barEditHandler = handler;
  }

  editDraft(draft: DraftGroup): void {
    this.edit(() => this.draft.set(draft));
  }

  editBar(state: SearchBarState): void {
    this.edit(() => this.loadBar(state), this.barEditHandler);
  }

  editSort(sort: SortQualifier[]): void {
    this.edit(() => this.sort.set(sort));
  }

  clearByHand(): void {
    this.edit(() => this.clear());
  }

  readonly canMoveBarToFilter = computed(
    () => !!this.bar().expr && this.vocabulary() !== null
  );
  readonly canMoveFilterToBar = computed(() => !!this.wire());

  /**
   * ANDs the bar's filter terms into the composer, keeping the draft's unfinished rows, which
   * `wire()` leaves out. Terms the vocabulary cannot edit arrive as opaque nodes.
   */
  moveBarToFilter(): void {
    const merged = this.draftWithBar();
    if (merged) this.moveBetweenSides(() => this.takeBarInto(merged));
  }

  /** The draft with the bar's filter terms ANDed in; `null` when the bar holds none. */
  private draftWithBar(): DraftGroup | null {
    const vocabulary = this.vocabulary();
    const expr = this.bar().expr;
    if (!vocabulary || !expr) return null;
    const options = decodeOptionsFor(vocabulary);
    const pending = this.pending();
    const base = pending ? fromWire(pending.expr, options) : this.draft();
    const incoming = fromWire(expr, options);
    const incomingNodes =
      incoming.connector === 'and' && !incoming.negated ? incoming.children : [incoming];
    return incomingNodes.reduce<DraftGroup>(andChild, base);
  }

  /** Sets the draft that took in the bar's filter terms, which leave the bar. */
  private takeBarInto(draft: DraftGroup): void {
    this.pending.set(null);
    this.loaded.set(null);
    this.draft.set(draft);
    this.error.set(null);
    this.bar.set({ search: this.bar().search, sort: this.bar().sort });
  }

  /** ANDs the composer's filter into the bar's; the Sort tab stays as it is. */
  moveFilterToBar(): void {
    const wire = this.wire();
    if (!wire) return;
    this.moveBetweenSides(() => {
      this.bar.set({ ...this.bar(), expr: andFilters(this.bar().expr, wire) });
      this.clearConditions();
    });
  }

  /**
   * A move changes what the composer holds, so the preset detaches even if the request is equal.
   * Emptying the composer must not release the preset's grammar either: leaving Path would empty
   * the bar the filter just moved into, so the bar takes that grammar as its own.
   */
  private moveBetweenSides(change: () => void): void {
    const preset = this.presetGrammar();
    if (preset) this.ownGrammar.set(preset);
    change();
    this.editHandler();
  }

  /**
   * A hand edit, handed on only when the request changed: `request` keeps its previous value for
   * a JSON-equal one, as when the search bar re-emits after a whitespace change. Compared before
   * the template preview, so editing a hole still counts.
   */
  private edit(change: () => void, handler = this.editHandler): void {
    const before = this.request();
    change();
    if (this.request() !== before) handler();
  }

  constructor() {
    const release = this.grammarService.onBeforeSwitch(() => {
      if (this.ownGrammar() === null) this.ownGrammar.set(this.grammarService.grammar());
    });
    inject(DestroyRef).onDestroy(release);

    effect(() => {
      const vocabulary = this.vocabulary();
      const pending = this.pending();
      if (!vocabulary || !pending) return;
      untracked(() => {
        const draft = fromWire(pending.expr, decodeOptionsFor(vocabulary));
        this.draft.set(draft);
        this.loaded.set({ expr: pending.expr, draft });
        this.pending.set(null);
      });
    });

    effect(() => {
      const preset = this.presetGrammar();
      if (!preset) return;
      const empty = preset === 'path' ? this.isEmpty() : !this.classicFiltered()();
      if (empty) untracked(() => this.presetGrammar.set(null));
    });

    // Hidden outside Path, a kept bar would be ANDed in again, unseen, once Path comes back.
    let inPath = false;
    effect(() => {
      const path = this.activeGrammar() === 'path';
      if (inPath && !path) untracked(() => this.bar.set({ search: [], sort: [] }));
      inPath = path;
    });

    effect(() => {
      const held = this.heldPreset();
      if (!held || !this.settled()) return;
      untracked(() => this.applyPreset(held.applied, held.target));
    });
  }

  /** Loads the composer side. */
  load(expr: FilterExpr | undefined, sort: SortQualifier[]): void {
    this.pending.set({ expr });
    this.sort.set(sort);
    this.error.set(null);
  }

  loadBar(state: SearchBarState): void {
    this.bar.set(state);
    this.error.set(null);
  }

  /** Empties the composer side; the search bar has its own clear. */
  clear(): void {
    this.clearConditions();
    this.sort.set([]);
  }

  private clearConditions(): void {
    this.pending.set(null);
    this.loaded.set(null);
    this.draft.set(draftGroup());
    this.error.set(null);
  }

  /** Empties both sides, for a bar that now filters another entity type or grammar. */
  reset(): void {
    this.clear();
    this.bar.set({ search: [], sort: [] });
  }

  /**
   * A failed Path request. The GBE error interceptor already notifies every failure; a Path one is
   * also shown in the panel, which outlines the conditions it names.
   */
  reportError(error: unknown, fallback: string): void {
    if (this.activeGrammar() !== 'path') return;
    const message = (error as Partial<GbeError> | null)?.errorMessage;
    this.error.set(message || fallback);
  }

  /** No clear-confirmation: a preset replaces the filter by definition. */
  applyPreset(applied: AppliedFilterPreset, target: PresetTarget): void {
    // A preset of either grammar waits too: applied in Classic on a bar about to settle in Path,
    // its search would land in the hidden Classic state.
    if (applied.grammar !== 'classic' && !this.settled()) {
      this.heldPreset.set({ applied, target });
      return;
    }
    this.heldPreset.set(null);
    if (applied.grammar === 'path' && !this.pathPossible()) {
      target.refuse();
      return;
    }
    // Deselecting a preset hands the bar back to the grammar set in the preferences.
    if (applied.detached) this.presetGrammar.set(null);
    if (applied.grammar) this.presetGrammar.set(applied.grammar);
    const grammar = applied.grammar ?? this.activeGrammar();
    if (grammar === 'path') {
      this.load(applied.pathFilter, (applied.pathSort ?? []).map(toSortQualifier));
      // Stored text has no place in the composer, so it replaces what the bar holds. The data
      // layer matches `search` as one substring, so the text comes back as the single phrase it
      // is searched as, not as the words it was typed in.
      const search = applied.search?.trim();
      if (search) this.loadBar({ search: [search], sort: [] });
    } else {
      this.reset();
    }
    target.apply({ ...applied, grammar });
  }
}
